import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  IconButton,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: theme.spacing(1),
  },
  commentField: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  actionButton: {
    marginLeft: theme.spacing(1),
  },
  statusIcon: {
    marginRight: theme.spacing(1),
  },
}));

interface TaskCreationDialogProps {
  open: boolean;
  isCreating: boolean;
  onClose: () => void;
  onConfirm: (comment: string) => void;
}

export const TaskCreationDialog: React.FC<TaskCreationDialogProps> = ({
  open,
  isCreating,
  onClose,
  onConfirm,
}) => {
  const classes = useStyles();
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');

  const MAX_LENGTH = 10000;

  useEffect(() => {
    if (!open) {
      setComment('');
      setError('');
    }
  }, [open]);

  const handleSubmit = () => {
    if (!comment.trim()) {
      setError('Comment is required to create a task');
      return;
    }

    if (comment.length > MAX_LENGTH) {
      setError(`Comment exceeds maximum length of ${MAX_LENGTH.toLocaleString()} characters`);
      return;
    }

    onConfirm(comment.trim());
  };

  const handleClose = () => {
    setComment('');
    setError('');
    onClose();
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (newValue.length <= MAX_LENGTH) {
      setComment(newValue);
      if (error) setError('');
    }
  };

  const getHelperText = () => {
    if (error) return error;
    
    const remaining = MAX_LENGTH - comment.length;
    const characterCount = `${comment.length.toLocaleString()}/${MAX_LENGTH.toLocaleString()} characters`;
    
    if (remaining === 0) {
      return `${characterCount} - Character limit reached`;
    } else if (remaining < 100) {
      return `${characterCount} - ${remaining} remaining`;
    } else {
      return characterCount;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle 
        className={classes.dialogTitle} 
        style={{ position: 'relative', paddingRight: '48px' }}
      >
        <Box display="flex" alignItems="center" mb={0.5}>
          <AddIcon className={classes.statusIcon} />
          <Typography variant="h6" style={{ marginLeft: 8 }}>
            Create Task
          </Typography>
        </Box>
        <IconButton 
          onClick={handleClose} 
          size="medium"
          aria-label="close"
          style={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Please provide a reason for creating this task. This comment will be added to the issue history along with the task details.
        </Typography>

        <TextField
          className={classes.commentField}
          label="Reason for creating task*"
          placeholder="Explain why you are creating a task for this error..."
          multiline
          rows={4}
          variant="outlined"
          fullWidth
          value={comment}
          onChange={handleCommentChange}
          error={!!error}
          helperText={getHelperText()}
          disabled={isCreating}
          inputProps={{
            maxLength: MAX_LENGTH,
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isCreating}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isCreating || !comment.trim()}
          style={{ 
            backgroundColor: (isCreating || !comment.trim()) ? undefined : '#28a745', 
            color: (isCreating || !comment.trim()) ? undefined : 'white'
          }}
          className={classes.actionButton}
        >
          {isCreating ? 'Creating Task...' : 'Create Task'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};